import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, getRouteApi, useBlocker, useNavigate } from "@tanstack/react-router";
import {
  ApiError,
  getTemplate,
  getTemplateVersion,
  listFonts,
  listTemplateAssets,
  publishTemplateVersion,
  updateTemplateVersion,
  type TemplateDefinition,
  type TemplateDetail,
  type TemplateVersionDetail,
} from "@/api/client";
import { useSession } from "@/auth/session";
import { LG, useMediaQuery } from "@/components/useMediaQuery";
import { EmptyState, ErrorBanner, ToolbarButton, WorkspaceHeader } from "@/components/ui";
import { TemplatePreview, type PreviewOptions } from "@/features/templates/TemplatePreview";
import { Canvas } from "@/features/templates/designer/Canvas";
import { canRedo, canUndo, designerReducer, initDesigner, isDirty } from "@/features/templates/designer/definition";
import { DEFAULT_ZOOM, ZOOMS } from "@/features/templates/designer/geometry";
import { FieldInspector, LayersPanel, PagePanel, Palette, Panel } from "@/features/templates/designer/Inspector";
import { checkDefinition, serverProblems, targetOf, type Problem } from "@/features/templates/designer/problems";

const NO_PROBLEMS: Problem[] = [];

const designerApi = getRouteApi("/staff/templates/$templateId/versions/$versionId/edit");

export function TemplateDesignerPage() {
  const { templateId, versionId } = designerApi.useParams();
  const template = useQuery({ queryKey: ["template", templateId], queryFn: () => getTemplate(templateId) });
  const version = useQuery({
    queryKey: ["template-version", templateId, versionId],
    queryFn: () => getTemplateVersion(templateId, versionId),
  });

  // 404 is an unknown id, 422 a malformed one; both mean the link points at nothing.
  const missing = [template.error, version.error].some((e) => e instanceof ApiError && [404, 422].includes(e.status));
  if (missing) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Layout not found" detail="No template version matches this link." />
        <Link to="/templates/$templateId" params={{ templateId }} search={{}} className="text-sm font-medium text-zinc-950 underline">
          Back to the template
        </Link>
      </section>
    );
  }
  if (!template.data || !version.data?.definition) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title="Layout" detail={versionId} />
        <ErrorBanner error={template.error ?? version.error} />
        {template.isLoading || version.isLoading ? <EmptyState title="Loading layout" detail="Fetching the version." /> : null}
      </section>
    );
  }
  return <Designer key={version.data.id} template={template.data} version={version.data} initial={version.data.definition} />;
}

function Designer({
  template,
  version,
  initial,
}: {
  template: TemplateDetail;
  version: TemplateVersionDetail;
  initial: TemplateDefinition;
}) {
  const readOnly = version.status !== "draft";
  const label = `v${version.version_no}`;
  const wide = useMediaQuery(LG);
  const { can } = useSession();
  const queryClient = useQueryClient();
  const [state, dispatch] = useReducer(designerReducer, initial, initDesigner);
  const dirty = isDirty(state);
  const fieldCount = state.present.fields.length;
  const [zoom, setZoom] = useState(DEFAULT_ZOOM);
  // Fixed for the session so the sample issue date doesn't tick while editing.
  const [now] = useState(() => new Date());
  const fonts = useQuery({ queryKey: ["fonts"], queryFn: listFonts });
  const assets = useQuery({ queryKey: ["template-assets"], queryFn: listTemplateAssets });

  const search = designerApi.useSearch();
  const navigate = useNavigate({ from: "/templates/$templateId/versions/$versionId/edit" });
  const selected = search.field !== undefined && search.field < fieldCount ? search.field : undefined;
  const selectedField = selected === undefined ? undefined : state.present.fields[selected];
  const select = useCallback(
    (field: number | undefined) => {
      if (field !== search.field) void navigate({ search: { field }, replace: true });
    },
    [navigate, search.field],
  );

  // Undo or redo can take away the selected field; drop the stale index rather than let a later field inherit it.
  useEffect(() => {
    if (search.field !== undefined && search.field >= fieldCount) select(undefined);
  }, [fieldCount, search.field, select]);

  const [refusal, setRefusal] = useState<{ fieldCount: number; problems: Problem[]; error: unknown } | null>(null);
  // The server's paths name fields by index, so they go stale once a field is added or removed, even mid-save.
  if (refusal && refusal.fieldCount !== fieldCount) setRefusal(null);
  const refused = refusal?.fieldCount === fieldCount ? refusal.problems : NO_PROBLEMS;
  const [preview, setPreview] = useState<PreviewOptions | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  const versionKey = ["template-version", template.id, version.id];

  // Shared by "Save draft" and "Save and preview"; also redraws any preview that's open.
  const store = async (definition: TemplateDefinition) => {
    await updateTemplateVersion(template.id, version.id, definition);
    // Marks what was sent, so an edit made while saving stays unsaved.
    dispatch({ type: "saved", definition });
    setRefusal(null);
    // Re-entering the designer starts from this cache, not the pre-save definition.
    queryClient.setQueryData<TemplateVersionDetail>(versionKey, (old) => old && { ...old, definition });
    // Not awaited: saving shouldn't wait for a whole PDF render.
    void queryClient.invalidateQueries({ queryKey: ["template-preview", template.id, version.id] });
    await queryClient.invalidateQueries({ queryKey: ["template", template.id] });
  };
  const save = useMutation({
    mutationFn: async ({ definition }: { definition: TemplateDefinition; thenPreview?: boolean }) => store(definition),
    onSuccess: (_, { thenPreview }) => {
      if (thenPreview) setPreview((open) => open ?? {});
    },
    onError: (error, sent) => setRefusal({ fieldCount: sent.definition.fields.length, problems: serverProblems(error), error }),
  });
  const publish = useMutation({
    // fieldCount is what was sent, so a refusal is matched against those fields.
    mutationFn: async ({ unsaved }: { unsaved: TemplateDefinition | null; fieldCount: number }) => {
      if (unsaved) await store(unsaved);
      return publishTemplateVersion(template.id, version.id);
    },
    onSuccess: async (published) => {
      setConfirming(false);
      setNotice(`${label} is now the current version.`);
      // The status flips the page to read-only straight away; the refetch fills in who published.
      queryClient.setQueryData<TemplateVersionDetail>(versionKey, (old) => (old ? { ...old, status: published.status } : old));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: versionKey }),
        queryClient.invalidateQueries({ queryKey: ["template", template.id] }),
        queryClient.invalidateQueries({ queryKey: ["templates"] }),
      ]);
    },
    onError: async (error, sent) => {
      // A 409 means the version changed elsewhere (e.g. published); reload it. Other errors keep the confirm open.
      if (error instanceof ApiError && error.status === 409) {
        setConfirming(false);
        await queryClient.invalidateQueries({ queryKey: versionKey });
        return;
      }
      setRefusal({ fieldCount: sent.fieldCount, problems: serverProblems(error), error });
    },
  });
  const busy = save.isPending || publish.isPending;
  const failed = save.error ?? publish.error;
  // A dropped refusal's "N problems" would name problems no longer listed.
  const stale = failed instanceof ApiError && failed.status === 422 && refusal?.error !== failed;
  // Once publish succeeds the version is read-only, so an edit made meanwhile would never be saved.
  const locked = readOnly || publish.isPending;

  useEffect(() => {
    // The narrow guard hides the canvas, so its shortcuts would change what can't be seen.
    if (locked || !wide) return;
    const onKey = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey) return;
      // Inputs keep their own text undo.
      if (e.target instanceof Element && e.target.closest("input, select, textarea, [contenteditable='true']")) return;
      const key = e.key.toLowerCase();
      if (key === "z" || (key === "y" && !e.shiftKey)) {
        e.preventDefault();
        dispatch({ type: key === "z" && !e.shiftKey ? "undo" : "redo" });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [locked, wide]);

  const start = () => {
    setNotice(null);
    save.reset();
    publish.reset();
  };

  const problems = useMemo(() => {
    const checks = readOnly ? [] : checkDefinition(state.present);
    const same = (a: Problem, b: Problem) => a.path === b.path && a.issue === b.issue;
    return [
      ...refused.map((p) => ({ ...p, source: "server" as const })),
      ...checks.filter((c) => !refused.some((r) => same(r, c))).map((p) => ({ ...p, source: "check" as const })),
    ];
  }, [refused, state.present, readOnly]);
  const issues = useCallback((path: string) => problems.filter((p) => p.path === path).map((p) => p.issue), [problems]);
  const invalid = useMemo(() => {
    const out = new Set<number>();
    for (const p of problems) {
      const target = targetOf(p.path);
      if (target.panel === "field") out.add(target.index);
    }
    return out;
  }, [problems]);

  // Changing the selection only rewrites ?field, so only leaving the page asks.
  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => current.pathname !== next.pathname,
    disabled: !dirty,
    withResolver: true,
  });

  const saveStatus = busy
    ? "Saving..."
    : dirty
      ? "Unsaved changes"
      : save.isSuccess || publish.isSuccess
        ? "All changes saved"
        : "No unsaved changes";

  // Hooks above stay unconditional; the canvas needs room a phone doesn't have.
  if (!wide) {
    return (
      <section className="space-y-5">
        <WorkspaceHeader title={`Layout of ${label}`} detail={`${template.name}, ${version.status}`} />
        {blocker.status === "blocked" ? <LeaveConfirm label={label} onStay={blocker.reset} onLeave={blocker.proceed} /> : null}
        <div className="rounded-lg border border-zinc-200 bg-white p-4 text-sm text-zinc-700">
          <p>The template designer needs a larger screen.</p>
          {dirty ? <p className="mt-2 font-medium text-amber-700">You have unsaved changes.</p> : null}
          <Link to="/templates" className="mt-3 inline-flex min-h-10 items-center font-medium text-zinc-950 underline">
            Back to templates
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="space-y-5">
      <WorkspaceHeader
        title={`Layout of ${label}`}
        detail={`${template.name}, ${version.status}`}
        action={
          <Link
            to="/templates/$templateId"
            params={{ templateId: template.id }}
            search={{}}
            className="text-sm font-medium text-zinc-950 underline"
          >
            Back to {template.name}
          </Link>
        }
      />
      {blocker.status === "blocked" ? <LeaveConfirm label={label} onStay={blocker.reset} onLeave={blocker.proceed} /> : null}
      {notice ? (
        <p role="status" className="text-sm font-medium text-emerald-700">
          {notice}
        </p>
      ) : null}
      {readOnly ? (
        <p className="rounded-lg border border-zinc-200 bg-white px-4 py-3 text-sm text-zinc-700">
          {label} is {version.status}, so its layout can't be changed. Start a new draft from it on the template page.
        </p>
      ) : null}
      <ErrorBanner error={fonts.error ?? assets.error} />
      <ErrorBanner error={stale ? null : failed} />
      <div className="flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm">
          <span className="font-medium text-zinc-700">Zoom</span>
          <select
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            className="rounded-md border border-zinc-300 bg-white px-2 py-1.5 text-sm outline-none focus:border-brand focus:ring-1 focus:ring-brand"
          >
            {ZOOMS.map((z) => (
              <option key={z} value={z}>
                {z * 100}%
              </option>
            ))}
          </select>
        </label>
        {readOnly ? null : (
          <>
            <ToolbarButton disabled={publish.isPending || !canUndo(state)} onClick={() => dispatch({ type: "undo" })}>
              Undo
            </ToolbarButton>
            <ToolbarButton disabled={publish.isPending || !canRedo(state)} onClick={() => dispatch({ type: "redo" })}>
              Redo
            </ToolbarButton>
            <ToolbarButton
              disabled={!dirty || busy}
              onClick={() => {
                start();
                save.mutate({ definition: state.present });
              }}
            >
              Save draft
            </ToolbarButton>
            <ToolbarButton
              disabled={busy}
              onClick={() => {
                start();
                if (dirty) save.mutate({ definition: state.present, thenPreview: true });
                else setPreview((open) => open ?? {});
              }}
            >
              {dirty ? "Save and preview" : "Preview"}
            </ToolbarButton>
            {can("template.publish") ? (
              <ToolbarButton
                aria-label={`Publish ${label}`}
                disabled={busy || confirming}
                onClick={() => {
                  start();
                  setConfirming(true);
                }}
              >
                Publish
              </ToolbarButton>
            ) : null}
            <span role="status" className={dirty ? "text-sm font-medium text-amber-700" : "text-sm text-zinc-500"}>
              {saveStatus}
            </span>
          </>
        )}
      </div>
      {confirming && !readOnly ? (
        <PublishConfirm
          label={label}
          dirty={dirty}
          busy={busy}
          publishing={publish.isPending}
          onPublish={() => publish.mutate({ unsaved: dirty ? state.present : null, fieldCount })}
          onCancel={() => setConfirming(false)}
        />
      ) : null}
      {preview ? (
        <TemplatePreview
          template={template}
          versionId={version.id}
          options={preview}
          onOptions={setPreview}
          onClose={() => setPreview(null)}
        />
      ) : null}
      {problems.length > 0 ? <ProblemList problems={problems} onSelect={select} /> : null}
      <div className="grid items-start gap-5 xl:grid-cols-[minmax(0,1fr)_22rem]">
        <Canvas
          definition={state.present}
          zoom={zoom}
          fonts={fonts.data ?? []}
          assets={assets.data ?? []}
          certType={template.cert_type}
          now={now}
          selected={selected}
          invalid={invalid}
          readOnly={locked}
          onSelect={select}
          dispatch={dispatch}
        />
        <aside aria-label="Inspector" className="space-y-4">
          {readOnly ? null : (
            <Palette
              page={state.present.page}
              assets={assets.data ?? []}
              readOnly={locked}
              onAdd={(field) => {
                dispatch({ type: "add", field });
                select(fieldCount);
              }}
            />
          )}
          {selectedField && selected !== undefined ? (
            <FieldInspector
              key={selected}
              index={selected}
              field={selectedField}
              page={state.present.page}
              fonts={fonts.data ?? []}
              assets={assets.data ?? []}
              readOnly={locked}
              dispatch={dispatch}
              issues={issues}
              onRemove={() => {
                dispatch({ type: "remove", index: selected });
                select(undefined);
              }}
            />
          ) : (
            <Panel title="Field">
              <p className="text-sm text-zinc-500">Select a field on the page to edit it.</p>
            </Panel>
          )}
          <PagePanel page={state.present.page} readOnly={locked} dispatch={dispatch} issues={issues} />
          <LayersPanel definition={state.present} assets={assets.data ?? []} readOnly={locked} dispatch={dispatch} issues={issues} />
        </aside>
      </div>
    </section>
  );
}

/** The template page's publish confirm, plus a note that unsaved edits go first. */
function PublishConfirm({
  label,
  dirty,
  busy,
  publishing,
  onPublish,
  onCancel,
}: {
  label: string;
  dirty: boolean;
  busy: boolean;
  publishing: boolean;
  onPublish: () => void;
  onCancel: () => void;
}) {
  const confirm = useRef<HTMLButtonElement>(null);
  useEffect(() => confirm.current?.focus(), []);
  return (
    <div
      role="group"
      aria-label={`Confirm publish ${label}`}
      className="flex flex-wrap items-center gap-3 rounded-lg border border-zinc-200 bg-zinc-50 px-4 py-3 text-sm"
    >
      <p className="min-w-72 flex-1 text-zinc-700">
        Publish {label}? It becomes the current version for new batches. Certificates already issued keep the version they
        were issued with.{dirty ? " Your unsaved changes are saved first." : ""}
      </p>
      <ToolbarButton tone="primary" ref={confirm} aria-label={`Confirm publish ${label}`} disabled={busy} onClick={onPublish}>
        {publishing ? "Publishing..." : `Publish ${label}`}
      </ToolbarButton>
      <ToolbarButton disabled={busy} onClick={onCancel}>
        Cancel
      </ToolbarButton>
    </div>
  );
}

function ProblemList({
  problems,
  onSelect,
}: {
  problems: (Problem & { source: "server" | "check" })[];
  onSelect: (index: number) => void;
}) {
  const refused = problems.some((p) => p.source === "server");
  return (
    <section aria-labelledby="designer-problems" className="space-y-2 rounded-lg border border-red-200 bg-white p-4">
      <h2 id="designer-problems" className="text-sm font-semibold text-red-800">
        Problems
      </h2>
      <p className="text-xs text-zinc-500">
        {refused
          ? "The last save was refused for these. Each is also shown next to its field or setting."
          : "Found while editing; the server checks again when you save."}
      </p>
      <ul aria-labelledby="designer-problems" className="divide-y divide-zinc-100 text-xs">
        {problems.map((p, i) => {
          const target = targetOf(p.path);
          return (
            <li key={`${i}-${p.path}-${p.issue}`} className="flex flex-wrap gap-x-3 py-1.5">
              {target.panel === "field" ? (
                <button type="button" onClick={() => onSelect(target.index)} className="font-mono text-zinc-950 underline">
                  {p.path}
                </button>
              ) : (
                <span className="font-mono text-zinc-700">{p.path || "definition"}</span>
              )}
              <span className="text-zinc-800">{p.issue}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

/** In-page rather than window.confirm, so it matches the app and can be tested. */
function LeaveConfirm({ label, onStay, onLeave }: { label: string; onStay: () => void; onLeave: () => void }) {
  const stay = useRef<HTMLButtonElement>(null);
  useEffect(() => stay.current?.focus(), []);
  return (
    <div
      role="alertdialog"
      aria-labelledby="designer-leave-title"
      aria-describedby="designer-leave-detail"
      className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm"
    >
      <div className="min-w-72 flex-1">
        <h2 id="designer-leave-title" className="font-semibold text-amber-900">
          Leave without saving?
        </h2>
        <p id="designer-leave-detail" className="text-amber-800">
          Your changes to {label} have not been saved.
        </p>
      </div>
      <ToolbarButton ref={stay} onClick={onStay}>
        Stay
      </ToolbarButton>
      <ToolbarButton tone="danger" onClick={onLeave}>
        Leave without saving
      </ToolbarButton>
    </div>
  );
}
